import { graphql } from '@eluve/graphql.tada';

/**
 * FRAGMENTS
 */

export const baseTemplateFragment = graphql(`
  fragment BaseTemplateDetails on LlmOutputTemplates @_unmask {
    __typename
    id
    name
    description
    llmOutputType
    isActive
    createdAt
    tenantId
    isDraft
    createdBy
  }
`);

export const templateVariantFragment = graphql(`
  fragment LlmOutputTemplateVariant on LlmOutputTemplateVariants @_unmask {
    __typename
    outputTemplateId
    variantNumber
    template
    isCurrentDefault
    createdAt
    variantNotes
    isDraft
    creator {
      __typename
      id
      firstName
      lastName
    }
  }
`);

export const sharedTemplateUserFragment = graphql(`
  fragment SharedTemplateUser on UsersLlmOutputTemplates @_unmask {
    __typename
    userId
    tenantId
    llmOutputTemplateId
    createdAt
  }
`);

export const templateUsersFragment = graphql(
  `
    fragment TemplateUsers on LlmOutputTemplates @_unmask {
      __typename
      id
      users_llm_output_templates {
        ...SharedTemplateUser
      }
    }
  `,
  [sharedTemplateUserFragment],
);

export const tenantTemplateFragment = graphql(`
  fragment TenantTemplate on TenantLlmOutputTemplates @_unmask {
    __typename
    tenantId
    llmOutputTemplateId
    isEnabled
    createdAt
  }
`);

export const templateOrgsFragment = graphql(
  `
    fragment TemplateOrgs on LlmOutputTemplates @_unmask {
      __typename
      id
      tenant_llm_output_templates {
        ...TenantTemplate
      }
    }
  `,
  [tenantTemplateFragment],
);

export const llmOutputTemplateFragment = graphql(
  `
    fragment LlmOutputTemplate on LlmOutputTemplates @_unmask {
      ...BaseTemplateDetails
      variants(orderBy: { variantNumber: DESC }) {
        ...LlmOutputTemplateVariant
      }
      ...TemplateUsers
      ...TemplateOrgs
    }
  `,
  [
    baseTemplateFragment,
    templateVariantFragment,
    templateUsersFragment,
    templateOrgsFragment,
  ],
);

export const externalChartTemplateFragment = graphql(`
  fragment ExternalChartTemplate on ExternalChartTemplates @_unmask {
    __typename
    id
    externalEhrId
    externalChartTemplateId
    tenantId
  }
`);

export const llmOutputTemplateWithExternalChartTemplatesFragment = graphql(
  `
    fragment LlmOutputTemplateWithExternalChartTemplatesFragment on LlmOutputTemplates
    @_unmask {
      ...LlmOutputTemplate
      external_chart_templates {
        ...ExternalChartTemplate
      }
    }
  `,
  [llmOutputTemplateFragment, externalChartTemplateFragment],
);

export const activeDynamicOutputPromptTemplatesFragment = graphql(`
  fragment ActiveDynamicOutputPromptTemplates on query_root @_unmask {
    activePromptTemplates(
      where: { outputType: { _in: ["DYNAMIC_OUTPUT"] } }
      orderBy: { name: ASC }
    ) {
      __typename
      id
      isCurrentDefault
      name
      description
      outputType
    }
  }
`);

/**
 * QUERIES
 */

export const getBaseOutputTemplateQuery = graphql(
  `
    query getOutputTemplate($id: uuid!) {
      llmOutputTemplatesByPk(id: $id) {
        ...BaseTemplateDetails
      }
    }
  `,
  [baseTemplateFragment],
);

export const getOutputTemplateQuery = graphql(
  `
    query getOutputTemplate($id: uuid!) {
      llmOutputTemplatesByPk(id: $id) {
        ...LlmOutputTemplateWithExternalChartTemplatesFragment
      }
      ...ActiveDynamicOutputPromptTemplates
    }
  `,
  [
    llmOutputTemplateWithExternalChartTemplatesFragment,
    activeDynamicOutputPromptTemplatesFragment,
  ],
);

export const getOutputTemplateVariantQuery = graphql(
  `
    query getOutputTemplateVariant($id: uuid!, $variantNumber: numeric!) {
      llmOutputTemplatesByPk(id: $id) {
        __typename
        id
        name
        description
        llmOutputType
        variants(where: { variantNumber: { _eq: $variantNumber } }) {
          ...LlmOutputTemplateVariant
        }
      }
    }
  `,
  [templateVariantFragment],
);

/**
 * MUTATIONS
 */

export const createOutputTemplateMutation = graphql(`
  mutation createOutputTemplate($input: LlmOutputTemplatesInsertInput!) {
    insertLlmOutputTemplatesOne(object: $input) {
      __typename
      id
    }
  }
`);

export const createOutputTemplateVariantMutation = graphql(
  `
    mutation createOutputTemplateVariant(
      $outputTemplateId: uuid!
      $template: jsonb!
      $variantNotes: String
      $isDraft: Boolean
    ) {
      insertLlmOutputTemplateVariantsOne(
        object: {
          outputTemplateId: $outputTemplateId
          template: $template
          variantNotes: $variantNotes
          isDraft: $isDraft
        }
      ) {
        ...LlmOutputTemplateVariant
      }
    }
  `,
  [templateVariantFragment],
);

export const updateTemplateVariantMutation = graphql(
  `
    mutation updateTemplateVariant(
      $outputTemplateId: uuid!
      $variantNumber: numeric!
      $isDraft: Boolean!
      $template: jsonb!
      $isCurrentDefault: Boolean
    ) {
      updateLlmOutputTemplateVariantsByPk(
        pkColumns: {
          outputTemplateId: $outputTemplateId
          variantNumber: $variantNumber
        }
        _set: {
          isDraft: $isDraft
          template: $template
          isCurrentDefault: $isCurrentDefault
        }
      ) {
        ...LlmOutputTemplateVariant
      }
    }
  `,
  [templateVariantFragment],
);

export const updateVariantNotesMutation = graphql(
  `
    mutation updateVariantNotes(
      $outputTemplateId: uuid!
      $variantNumber: numeric!
      $variantNotes: String
    ) {
      updateLlmOutputTemplateVariantsByPk(
        pkColumns: {
          outputTemplateId: $outputTemplateId
          variantNumber: $variantNumber
        }
        _set: { variantNotes: $variantNotes }
      ) {
        ...LlmOutputTemplateVariant
      }
    }
  `,
  [templateVariantFragment],
);

export const setOutputTemplateAsDefaultMutation = graphql(
  `
    mutation setOutputTemplateAsDefaultMutation(
      $outputTemplateId: uuid!
      $variantNumber: numeric!
    ) {
      updateLlmOutputTemplateVariants(
        _set: { isCurrentDefault: false }
        where: {
          _and: {
            isCurrentDefault: { _eq: true }
            outputTemplateId: { _eq: $outputTemplateId }
          }
        }
      ) {
        returning {
          __typename
          outputTemplateId
          variantNumber
          isCurrentDefault
        }
      }
      updateLlmOutputTemplateVariantsByPk(
        pkColumns: {
          outputTemplateId: $outputTemplateId
          variantNumber: $variantNumber
        }
        _set: { isCurrentDefault: true }
      ) {
        __typename
        outputTemplateId
        variantNumber
        isCurrentDefault
      }
    }
  `,
  [llmOutputTemplateFragment],
);

export const updateOutputTemplateMutation = graphql(`
  mutation updateOutputTemplate(
    $id: uuid!
    $name: String
    $description: String
  ) {
    updateLlmOutputTemplatesByPk(
      pkColumns: { id: $id }
      _set: { name: $name, description: $description }
    ) {
      __typename
      id
      name
      description
    }
  }
`);
